import { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import ImageProcessingApi from '../../../api/ImageProcessingApi';
import { ImageCategory } from '../../shared/state/utils';
import { AuthenticatedUser } from '../../../../../../../shared/types';
import { useTelemetryContext } from '../../../../../../../telemetry/telemetryContext';
import { getUserInfo } from '../../../../../../../telemetry/utils';
import { GetImagesResponse, imagesQueryKey } from '../queries/use-query-images';
import { photoStatusQueryKey } from '../../entry/queries/use-query-photo-status';

type IConfirmExtraImagePurchaseResult = {
    handleToggleCategory: (imageId: number) => void;
    handleConfirmExtraImagePurchase: () => void;
    handleCancelExtraImagePurchase: () => void;
    showConfirmExtraImagePurchaseDialog: boolean;
};

type IConfirmExtraImagePurchaseArgs = {
    orderId: number;
    user: AuthenticatedUser;
    onPurchaseSuccess?: (imageId: number) => void;
    onCategoryOverride?: (imageId: number) => void;
};

export function useConfirmExtraImagePurchase({
    orderId,
    user,
    onPurchaseSuccess,
    onCategoryOverride
}: IConfirmExtraImagePurchaseArgs): IConfirmExtraImagePurchaseResult {
    let [clickedImageForPurchase, setClickedImageForPurchase] = useState<number | null>(null);
    let [showConfirmExtraImagePurchaseDialog, setShowConfirmationExtraImagePurchaseDialog] =
        useState(false);
    let queryClient = useQueryClient();

    let { logger } = useTelemetryContext();

    let approveMutation = useMutation(
        () => {
            if (!clickedImageForPurchase) {
                throw new Error(`Can't use approve mutation until we have a selected image id!`);
            }

            let imagesToApprove = [{ id: clickedImageForPurchase, category: ImageCategory.Normal }];
            let extraImageCount = 1;

            return ImageProcessingApi.customerApproveImages(
                orderId,
                imagesToApprove,
                extraImageCount
            );
        },
        {
            onMutate: async () => {
                await queryClient.cancelQueries([imagesQueryKey, { orderId }]);

                let previousData = queryClient.getQueryData<GetImagesResponse>([
                    imagesQueryKey,
                    { orderId }
                ]);

                queryClient.setQueryData<GetImagesResponse>(
                    [imagesQueryKey, { orderId }],
                    (oldData) => {
                        if (!oldData) return oldData;
                        return oldData.map((product) => ({
                            ...product,
                            thumbnails: product.thumbnails.map((thumbnail) =>
                                thumbnail.id === clickedImageForPurchase
                                    ? {
                                          ...thumbnail,
                                          categoryId: ImageCategory.Normal,
                                          reference: ''
                                      }
                                    : thumbnail
                            )
                        }));
                    }
                );

                if (clickedImageForPurchase && onCategoryOverride) {
                    onCategoryOverride(clickedImageForPurchase);
                }

                return { previousData };
            },
            onError: (_error, _variables, context) => {
                if (context && context.previousData) {
                    queryClient.setQueryData([imagesQueryKey, { orderId }], context.previousData);
                }
                if (clickedImageForPurchase && onCategoryOverride) {
                    onCategoryOverride(clickedImageForPurchase);
                }
            },
            onSettled: () => {
                queryClient.invalidateQueries([imagesQueryKey, { orderId }]);
                queryClient.invalidateQueries([photoStatusQueryKey, { orderId }]);
                if (clickedImageForPurchase && onPurchaseSuccess) {
                    onPurchaseSuccess(clickedImageForPurchase);
                }
            }
        }
    );

    function handleToggleCategory(imageId: number) {
        setClickedImageForPurchase(imageId);
        setShowConfirmationExtraImagePurchaseDialog(true);
    }

    function handleConfirmExtraImagePurchase() {
        if (clickedImageForPurchase) {
            approveMutation.mutateAsync().catch((error) => {
                let additionalInfo = {
                    origin: 'CustomerImageList.useConfirmExtraImagePurchse.handleConfirm',
                    userInfo: getUserInfo(user)
                };

                logger.logException(error, additionalInfo);
            });
        }
        setShowConfirmationExtraImagePurchaseDialog(false);
    }

    function handleCancelExtraImagePurchase() {
        setShowConfirmationExtraImagePurchaseDialog(false);
    }

    return {
        handleToggleCategory,
        handleConfirmExtraImagePurchase,
        handleCancelExtraImagePurchase,
        showConfirmExtraImagePurchaseDialog
    };
}
